import { z } from 'zod'
import {
  SeriesAlignmentQuery,
  CreateSeriesIdentityRequest,
  SeriesAlignmentClass,
  SeriesAlignmentEdition,
  SeriesIdentity,
  UpdateSeriesAlignmentRequest,
} from './series-alignment.js'
import { ModelReconciliationRequest, ReconciliationChange } from './model-reconciliation.js'

const ids = z
  .array(z.guid())
  .min(1)
  .max(10)
  .refine(v => new Set(v).size === v.length)
export const MatchingIdentity = CreateSeriesIdentityRequest.extend({ id: z.guid() })
export const SeriesMatchingRequest = z.strictObject({
  showIds: ids,
  revision: z.string().regex(/^[a-f0-9]{64}$/),
  operations: UpdateSeriesAlignmentRequest.shape.operations,
  repairs: ModelReconciliationRequest.shape.classes.default([]),
  newIdentities: z.array(MatchingIdentity).max(1000).default([]),
  reason: z.string().trim().min(1).max(2000),
  notes: z.record(z.guid(), z.string().trim().min(1).max(2000)).default({}),
})
export const SeriesMatchingApplyRequest = SeriesMatchingRequest.extend({
  reviewToken: z.string().min(1),
  reviewedAt: z.string().datetime(),
})
export const SeriesMatchingQuery = SeriesAlignmentQuery.pick({ years: true })
export const MatchingClass = SeriesAlignmentClass.extend({
  rules: z.string().nullable(),
  eligibility: z.string().nullable(),
  exhibitType: z.string().nullable(),
  subtype: z.string().nullable(),
})
// Why this installation cannot save matching changes for an edition: it holds a read-only copy,
// the show is being handed over, or another installation runs it. Null when it runs the show.
export const MatchingOwnershipRestriction = z.enum([
  'read_only',
  'outgoing',
  'inactive',
  'not_owned',
])
export const MatchingEdition = SeriesAlignmentEdition.extend({
  ownershipRestriction: MatchingOwnershipRestriction.nullable(),
})
export const SeriesMatchingResponse = z.object({
  revision: z.string().regex(/^[a-f0-9]{64}$/),
  editions: z.array(MatchingEdition),
  classes: z.array(MatchingClass),
  identities: z.array(SeriesIdentity),
  counts: z.object({ review: z.number(), matched: z.number(), excluded: z.number() }),
  candidates: z.array(
    z.object({ classId: z.guid(), candidateId: z.guid(), evidence: z.enum(['model', 'name']) })
  ),
  canRepair: z.boolean(),
  canCreateIdentity: z.boolean(),
  models: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      retired: z.boolean(),
      subtypes: z.array(z.object({ id: z.string(), name: z.string(), retired: z.boolean() })),
    })
  ),
})
export const SeriesMatchingPreview = z.object({
  reviewToken: z.string(),
  reviewedAt: z.string().datetime(),
  changes: z.array(ReconciliationChange),
  blockers: z.array(
    z.object({ code: z.string(), message: z.string(), classIds: z.array(z.string()).default([]) })
  ),
  canApply: z.boolean(),
})
export const SeriesMatchingResult = z.object({
  receiptId: z.string(),
  alreadyApplied: z.boolean(),
  changed: z.number(),
  identityIds: z.array(z.string()),
})
export type TSeriesMatchingRequest = z.infer<typeof SeriesMatchingRequest>
export type TSeriesMatchingResponse = z.infer<typeof SeriesMatchingResponse>
export type TSeriesMatchingPreview = z.infer<typeof SeriesMatchingPreview>
export type TSeriesMatchingResult = z.infer<typeof SeriesMatchingResult>
export type TSeriesMatchingApplyRequest = z.infer<typeof SeriesMatchingApplyRequest>
